/** Stable event names shared by typed analytics callers. */
export const MIXPANEL_EVENTS = {
  // Trading Behavior
  TRADING: {
    ORDER_SUBMITTED: "order_submitted",
    ORDER_CANCELLED: "order_cancelled",
    POSITION_CLOSED: "close_position",
    TWAP_ORDER_SUBMITTED: "twap_order_submitted",
    STOP_ORDER_SUBMITTED: "stop_order_submitted",
    TPSL_UPDATE: "tpsl_update",
    TPSL_CANCEL: "tpsl_cancel",
    TWAP_ORDER_CANCELLED: "twap_order_cancelled",
    TPSL_SUBMITTED_WITH_ORDER_ID: "tpsl_submitted_with_order_id",
    TRANSACTION_SETTLED: "transaction_settled",
    ENCRYPTED_TXN_RETRY: "encrypted_txn_retry",
  },
  // Portfolio Events
  PORTFOLIO: {
    DEPOSIT: "deposit",
    WITHDRAW: "withdraw",
    MESH_DEPOSIT_CONFIRMED: "mesh_deposit_confirmed",
    MESH_DEPOSIT_INITIATED: "mesh_deposit_initiated",
    MESH_WITHDRAW_CONFIRMED: "mesh_withdraw_confirmed",
    MESH_WITHDRAW_INITIATED: "mesh_withdraw_initiated",
    MESH_WITHDRAW_ABANDONED: "mesh_withdraw_abandoned",
    // Cross-chain (NEAR Intents) deposit funnel, in order: address issued → address copied →
    // deposit detected → 1Click settled → swept into collateral.
    XCHAIN_DEPOSIT_ADDRESS_ISSUED: "xchain_deposit_address_issued",
    XCHAIN_DEPOSIT_ADDRESS_FAILED: "xchain_deposit_address_failed",
    XCHAIN_DEPOSIT_ADDRESS_COPIED: "xchain_deposit_address_copied",
    XCHAIN_DEPOSIT_DETECTED: "xchain_deposit_detected",
    XCHAIN_DEPOSIT_SETTLED: "xchain_deposit_settled",
    XCHAIN_DEPOSIT_SWEPT: "xchain_deposit_swept",
    XCHAIN_DEPOSIT_TRACKER_LINK_CLICKED: "xchain_deposit_tracker_link_clicked",
    // Fun deposit funnel, in order: entry seen → entry pressed → provider checkout opened →
    // provider reported the charge. `PAYMENT_CONFIRMED` is the provider's word for its own step and
    // never means the USDC reached Aptos — that half is the xchain funnel above.
    FUN_DEPOSIT_ENTRY_VIEWED: "fun_deposit_entry_viewed",
    FUN_DEPOSIT_ENTRY_CLICKED: "fun_deposit_entry_clicked",
    FUN_DEPOSIT_CHECKOUT_OPENED: "fun_deposit_checkout_opened",
    FUN_DEPOSIT_PAYMENT_CONFIRMED: "fun_deposit_payment_confirmed",
    FUN_DEPOSIT_PAYMENT_FAILED: "fun_deposit_payment_failed",
    FUN_DEPOSIT_PROVIDER_BLOCKED: "fun_deposit_provider_blocked",
    // Cross-chain (NEAR Intents) withdrawal funnel, in order:
    // quote obtained → sent to the 1Click address → 1Click settled.
    // A native Aptos withdrawal skips straight to `sent`: there is nothing to quote.
    XCHAIN_WITHDRAW_QUOTED: "xchain_withdraw_quoted",
    XCHAIN_WITHDRAW_QUOTE_FAILED: "xchain_withdraw_quote_failed",
    XCHAIN_WITHDRAW_SENT: "xchain_withdraw_sent",
    XCHAIN_WITHDRAW_SETTLED: "xchain_withdraw_settled",
  },
  // User Behavior
  USER_BEHAVIOR: {
    CREATE_SUBACCOUNT: "create_subaccount",
    SESSION_KEY_DELEGATION: "session_key_delegation",
    SESSION_KEY_IMPORTED: "session_key_imported",
    EDIT_SLIPPAGE: "edit_slippage",
    EDIT_SUBACCOUNT_NAME: "edit_subaccount_name",
  },
  // Trading Context Events
  TRADING_CONTEXT: {
    LEVERAGE_SET: "leverage_set",
  },
  // UI/UX Events
  UI: {
    MODAL_OPENED: "modal_opened",
    PAGE_VIEWED: "page_viewed",
    VAULT_CHART_RANGE_CHANGED: "vault_chart_range_changed",
    VAULT_CHART_TYPE_CHANGED: "vault_chart_type_changed",
    PORTFOLIO_CHART_RANGE_CHANGED: "portfolio_chart_range_changed",
    PORTFOLIO_CHART_TYPE_CHANGED: "portfolio_chart_type_changed",
    PNL_SHARE_LINK_COPIED: "social_share_link_copied",
    PNL_SHARE_CARD_COPIED: "social_shared_to_x",
    PNL_SHARED_TO_X: "social_shared_to_x",
    PNL_SHARE_CARD_DOWNLOADED: "pnl_share_card_downloaded",
    PNL_SHARE_CARD_SWITCHED_TO_CURRENCY: "pnl_share_card_switched_to_currency",
    WATCHLIST_MARKET_ADDED: "watchlist_market_added",
    WATCHLIST_MARKET_REMOVED: "watchlist_market_removed",
    MARKET_SEARCH: "market_search",
    TV_CHART_INTERVAL_CHANGED: "tv_chart_interval_changed",
    TV_CHART_OUTLIERS_TOGGLED: "tv_chart_outliers_toggled",
    ORDERBOOK_PANEL_TAB_CHANGED: "orderbook_panel_tab_changed",
    MOBILE_TRADE_PANEL_CHANGED: "mobile_trade_panel_changed",
    MARKET_SELECTOR_TAB_CHANGED: "market_selector_tab_changed",
    TRENDING_MARKET_CLICKED: "trending_market_clicked",
  },
  // Vaults
  VAULT: {
    DEPOSIT: "vault_deposit",
    WITHDRAW: "vault_withdraw",
    CREATE: "vault_create",
  },
  // Wallet Events
  WALLET: {
    CONNECTED: "wallet_connected",
  },
  // PWA Install Funnel
  PWA: {
    INSTALL_OFFERED: "pwa_install_offered",
    INSTALL_ACCEPTED: "pwa_install_accepted",
    INSTALL_DECLINED: "pwa_install_declined",
    INSTALLED: "pwa_installed",
    BANNER_SHOWN: "pwa_banner_shown",
    BANNER_CTA_CLICKED: "pwa_banner_cta_clicked",
    BANNER_DISMISSED: "pwa_banner_dismissed",
    DRAWER_CTA_CLICKED: "pwa_drawer_cta_clicked",
    MODAL_SHOWN: "pwa_modal_shown",
    INSTRUCTIONS_ACKNOWLEDGED: "pwa_instructions_acknowledged",
  },
  // Rewards
  REWARDS: {
    CLAIM_INITIATED: "rewards_claim_initiated",
    CLAIM_SUCCEEDED: "rewards_claim_succeeded",
    CLAIM_FAILED: "rewards_claim_failed",
    TAB_CHANGED: "rewards_tab_changed",
  },
  // Affiliates portal
  AFFILIATES: {
    BUILDER_LANDING_VIEWED: "affiliates_builder_landing_viewed",
    REFERRAL_LINK_COPIED: "affiliates_referral_link_copied",
  },
  // Funded First Trade Funnel
  FFT: {
    CARD_SHOWN: "fft_card_shown",
    CARD_CTA_CLICKED: "fft_card_cta_clicked",
    DASHBOARD_VIEWED: "fft_dashboard_viewed",
    LOCK_MODAL_CLOSED: "fft_lock_modal_closed",
    LOCK_INITIATED: "fft_lock_initiated",
    LOCK_SUCCEEDED: "fft_lock_succeeded",
    LOCK_FAILED: "fft_lock_failed",
    TRIAL_OPEN_INITIATED: "fft_trial_open_initiated",
    TRIAL_OPEN_SUCCEEDED: "fft_trial_open_succeeded",
    TRIAL_OPEN_FAILED: "fft_trial_open_failed",
    TRIAL_BLOCKED: "fft_trial_blocked",
    SETTLE_INITIATED: "fft_settle_initiated",
    SETTLE_SUCCEEDED: "fft_settle_succeeded",
    SETTLE_FAILED: "fft_settle_failed",
    SETTLE_OBSERVED: "fft_settle_observed",
    CLAIM_INITIATED: "fft_claim_initiated",
    CLAIM_SUCCEEDED: "fft_claim_succeeded",
    CLAIM_FAILED: "fft_claim_failed",
  },
  // Card Vault Funnel
  CARD_VAULT: {
    ENROLL_CTA_CLICKED: "card_vault_enroll_cta_clicked",
    SUBACCOUNT_SELECTED: "card_vault_subaccount_selected",
    ENROLL_CLICKED: "card_vault_enroll_clicked",
    ENROLL_SUCCEEDED: "card_vault_enroll_succeeded",
    ENROLL_FAILED: "card_vault_enroll_failed",
    NAV_TAB_CLICKED: "card_vault_nav_tab_clicked",
    TOAST_SHOWN: "card_vault_toast_shown",
    TOAST_CTA_CLICKED: "card_vault_toast_cta_clicked",
  },
  // Agent Chat
  AGENT: {
    THREAD_SWITCHED: "agent_thread_switched",
    THREAD_DEPTH_SAMPLED: "agent_thread_depth_sampled",
    SUGGESTION_SELECTED: "agent_suggestion_selected",
  },
} as const;
